import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'What to Put on a Missionary Prayer Card (And How to Actually Use It)';
const DESCRIPTION =
    "A prayer card is often the only thing a potential supporter keeps. What to include, what to leave off, and how to get it into the hands of people who'll actually pray — and give.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover25.jpg',
            datePublished: '2026-08-05',
            dateModified: '2026-08-05',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/missionary-prayer-card-what-to-include',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What size should a missionary prayer card be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Standard business card size (3.5" x 2") is the most common and the cheapest to print in bulk, but a postcard size (4" x 6") gives you room for a family photo on one side and prayer points on the other, and doubles as a mailer. Either works — pick whichever fits how you plan to hand it out.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many prayer points should a prayer card include?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Two or three, and specific. "Pray for our ministry" is forgettable; "pray for our visa renewal in March" is something a person can actually remember and pray about. Rotate the points as your situation changes rather than listing every ongoing need at once.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should a prayer card include a way to give?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. A prayer card's job isn't only prayer — it's also the easiest, lowest-pressure way for someone who already wants to give to actually do it. A short QR code or a simple, easy-to-type URL to your support page belongs on every card, alongside the prayer points, not instead of them.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How often should I update my prayer card?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Every 6-12 months, or immediately after any major change — a new field assignment, a new baby, a finished language school, a role change. An outdated prayer card with old prayer points or an old photo quietly signals that you haven't kept in touch, which works against the very relationship it's meant to maintain.",
                    },
                },
            ],
        },
    ],
};

export default function MissionaryPrayerCard() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/missionary-prayer-card-what-to-include' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-prayer-card-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover25.jpg" alt="A missionary's prayer card with a photo and prayer points laid on a table" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A support letter gets read once and often filed away. A newsletter gets skimmed and forgotten by next month. A prayer card, if it's good, sits on a refrigerator, in a Bible, or on a desk for a year — which makes it one of the highest-leverage, lowest-effort pieces of support-raising material a missionary can make, and one of the most commonly done badly.</p>

                    <h2>What actually belongs on a prayer card</h2>

                    <p>Keep it to what fits on a card someone glances at for five seconds, not a mini support letter:</p>

                    <ul>
                        <li><strong>A real, recent photo.</strong> A current headshot or family photo — not a stock image, not a photo from five years and two kids ago. This is the part people actually connect with.</li>
                        <li><strong>Your name and field or role</strong>, in a phrase a stranger can understand without context — "Church planters in Northern Thailand" tells a reader more in three words than "missionaries" alone.</li>
                        <li><strong>One sentence on what you actually do.</strong> Not your full calling story — save that for the support letter. One clear sentence.</li>
                        <li><strong>Two or three specific, current prayer points</strong> — see the next section, this is where most cards go wrong.</li>
                        <li><strong>A simple way to give or follow along</strong> — a QR code or a short, easy-to-type link to your support page.</li>
                    </ul>

                    <p>That's it. If you're also including a full paragraph of testimony, a Bible verse, a family history, and a detailed ministry description, the card has become a flyer no one finishes reading. Save the fuller story for your <a href="/resources/how-to-write-a-missionary-support-letter">support letter</a> or your <a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">fundraising page bio</a> — the prayer card's only job is to be remembered.</p>

                    <h2>Specific prayer points beat generic ones</h2>

                    <p>"Pray for our ministry" and "pray for the lost" are true, sincere, and completely unmemorable — a reader has nothing concrete to hold onto, so nothing happens after they read it. Compare that to points like these:</p>

                    <ul>
                        <li>"Pray for our visa renewal appointment on March 14."</li>
                        <li>"Pray for wisdom as we train three new local leaders this spring."</li>
                        <li>"Pray for our daughter Mia as she starts at a new school this fall."</li>
                    </ul>

                    <p>Specific points do two things generic ones don't: they're easy to actually remember and pray for on a random Tuesday, and they quietly demonstrate that your ministry is real and current, not a vague line item. Rotate your two or three points as circumstances change, rather than trying to cover everything at once.</p>

                    <h2>Physical vs. digital prayer cards</h2>

                    <p>You don't have to choose — most missionaries end up using both, for different moments:</p>

                    <ul>
                        <li><strong>Printed cards</strong> work best in person — handed out after a church service, left at a missions conference table, tucked into a Christmas card. They're tangible and they stick around on a fridge or a desk in a way a digital file never does.</li>
                        <li><strong>A digital version</strong> — the same layout as a single image or a simple one-page PDF — is what you actually send by text or share on social media, where a printed card can't go. Keep the exact same photo and prayer points on both so anyone who's seen you in person recognizes the digital version too.</li>
                    </ul>

                    <h2>Put your giving link on the card, not just your story</h2>

                    <p>A prayer card that only asks for prayer misses the person standing right there who was already planning to give something before they read it — they just need the path to be short. A QR code that opens your Devotion Fund page directly, or a link simple enough to type from memory later, removes the single biggest source of drop-off between "I want to support this" and an actual first gift. Pair the ask with the prayer points instead of treating them as separate requests; most people who pray for you regularly are also the people most likely to give.</p>

                    <h2>Update it on a schedule, not "eventually"</h2>

                    <p>Set a recurring reminder to review your prayer card every 6-12 months, and update it immediately after anything major — a new assignment, a finished language-learning milestone, a new child, a role change. An old photo or a prayer point that resolved a year ago tells a supporter, even subtly, that they haven't heard from you in a while. Refreshing the card is also a natural moment to send it along with your next <a href="/resources/free-missionary-newsletter-template">newsletter update</a>, so it reaches your existing supporters, not just new contacts.</p>

                    <h2>Common mistakes to avoid</h2>

                    <ul>
                        <li><strong>Too much text.</strong> If it needs more than five seconds to read, it's a flyer, not a card.</li>
                        <li><strong>A dated or overly posed photo.</strong> Recent and natural beats professional and old.</li>
                        <li><strong>No way to actually give.</strong> Prayer without a next step leaves engaged people with nowhere to go.</li>
                        <li><strong>Vague prayer points that never change.</strong> If the same two lines have been on the card for two years, they've stopped meaning anything.</li>
                        <li><strong>Printing hundreds before testing one.</strong> Print a small batch first, hand a few out, and see what people actually remember before committing to a large print run.</li>
                    </ul>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What size should a missionary prayer card be?</strong><br />
                    Standard business card size (3.5" x 2") is cheapest and most common; a postcard size (4" x 6") gives more room for a photo and doubles as a mailer.</p>

                    <p><strong>How many prayer points should a prayer card include?</strong><br />
                    Two or three, and specific enough that someone can actually remember and pray them — not a general "pray for our ministry."</p>

                    <p><strong>Should a prayer card include a way to give?</strong><br />
                    Yes — a QR code or short link to your support page, alongside the prayer points, so someone ready to give has an immediate next step.</p>

                    <p><strong>How often should I update my prayer card?</strong><br />
                    Every 6-12 months, or right after any major change, so the photo and prayer points stay current.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter</a></li>
                        <li><a href="/resources/free-missionary-newsletter-template">Free Missionary Newsletter Template</a></li>
                        <li><a href="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations">How to Write a Fundraising Page Bio That Gets People to Give</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after noticing how many missionaries pour hours into their support letter and newsletter but treat the prayer card as an afterthought — even though it's often the single piece of paper that outlasts everything else you hand someone.</p>
                    <p><small><em>Last updated: 2026-08-05.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/missionary-prayer-card-what-to-include" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
